import React, { lazy, Suspense, useEffect, useState } from "react";
import { Navigate, RouterProvider, useLocation } from "@/lib/router";
import { AuthProvider, useAuth } from "@/lib/auth";
import api from "@/lib/api";
import { ConfirmProvider } from "@/components/ConfirmModal";
import { JobsProvider } from "@/lib/jobs";
import { ServerProvider } from "@/lib/serverContext";
import { ThemeProvider } from "@/lib/theme";
import { LocaleProvider } from "@/lib/locale";
import { clearClientRuntimeCaches, recordClientCrash } from "@/lib/clientRecovery";

// Every product surface is an independent route chunk. Keeping these imports
// here (instead of a second route registry) also makes capability guards and
// the component they protect reviewable in one place.
// The authenticated shell and global overlays are also deferred. Login/public
// status must not download the full navigation icon set, ANSI renderer or
// native download helpers before they are actually needed.
const Layout = lazy(() => import("@/components/Layout"));
const JobsWidget = lazy(() => import("@/components/JobsWidget"));
const DownloadTransferTray = lazy(
  () => import("@/components/DownloadTransferTray"),
);
const GlobalMediaPlayerHost = lazy(
  () => import("@/components/GlobalMediaPlayerHost"),
);
const Login = lazy(() => import("@/pages/Login"));
const Dashboard = lazy(() => import("@/pages/Dashboard"));
const Nginx = lazy(() => import("@/pages/Nginx"));
const Docker = lazy(() => import("@/pages/Docker"));
const AppCatalog = lazy(() => import("@/pages/AppCatalog"));
const Supervisor = lazy(() => import("@/pages/Supervisor"));
const MariaDB = lazy(() =>
  import("@/pages/Database").then((module) => ({ default: module.MariaDB })),
);
const PostgreSQL = lazy(() =>
  import("@/pages/Database").then((module) => ({ default: module.PostgreSQL })),
);
const Logs = lazy(() => import("@/pages/Logs"));
const Settings = lazy(() => import("@/pages/Settings"));
const Redis = lazy(() => import("@/pages/Redis"));
const PHP = lazy(() => import("@/pages/PHP"));
const Backup = lazy(() => import("@/pages/Backup"));
const Notifications = lazy(() => import("@/pages/Notifications"));
const NotificationHistory = lazy(() => import("@/pages/NotificationHistory"));
const Files = lazy(() => import("@/pages/Files"));
const Storage = lazy(() => import("@/pages/Storage"));
const CloudflareCenter = lazy(() => import("@/pages/Cloudflare"));
const WireGuard = lazy(() => import("@/pages/WireGuard"));
const Systemd = lazy(() => import("@/pages/Systemd"));
const Websites = lazy(() => import("@/pages/Websites"));
const Migrations = lazy(() => import("@/pages/Migrations"));
const Network = lazy(() => import("@/pages/Network"));
const PM2Manager = lazy(() => import("@/pages/PM2Manager"));
const Helpers = lazy(() => import("@/pages/Helpers"));
const CronJobs = lazy(() => import("@/pages/CronJobs"));
const SSL = lazy(() => import("@/pages/SSL"));
const Git = lazy(() => import("@/pages/Git"));
const Scripts = lazy(() => import("@/pages/Scripts"));
const ApiTest = lazy(() => import("@/pages/ApiTest"));
const PublicAPI = lazy(() => import("@/pages/PublicAPI"));
const Servers = lazy(() => import("@/pages/Servers"));
const Packages = lazy(() => import("@/pages/Packages"));
const ComponentCenter = lazy(() => import("@/pages/ComponentCenter"));
const Timers = lazy(() => import("@/pages/Timers"));
const Plugins = lazy(() => import("@/pages/Plugins"));
const Deployments = lazy(() => import("@/pages/Deployments"));
const Caddy = lazy(() => import("@/pages/Caddy"));
const Users = lazy(() => import("@/pages/Users"));
const Dns = lazy(() => import("@/pages/Dns"));
const SecurityCenter = lazy(() => import("@/pages/SecurityCenter"));
const Waf = lazy(() => import("@/pages/Waf"));
const Observability = lazy(() => import("@/pages/Observability"));
const Roles = lazy(() => import("@/pages/Roles"));
const PublicStatus = lazy(() => import("@/pages/PublicStatus"));
const Platform = lazy(() => import("@/pages/Platform"));
const MailControlCenter = lazy(() => import("@/pages/MailControlCenter"));
const AndroidManagement = lazy(() => import("@/pages/AndroidManagement"));
const LoadBalancers = lazy(() => import("@/pages/LoadBalancers"));
const VsCode = lazy(() => import("@/pages/VsCode"));
const AiCoding = lazy(() => import("@/pages/AiCoding"));
const TerminalPage = lazy(() => import("@/pages/TerminalPage"));

class AppErrorBoundary extends React.Component<
  { children: React.ReactNode },
  { error: Error | null }
> {
  state: { error: Error | null } = { error: null };

  static getDerivedStateFromError(error: Error) {
    return { error };
  }

  componentDidCatch(error: Error, info: React.ErrorInfo) {
    recordClientCrash(error, "react-boundary", info.componentStack || "");
  }

  private recover = async () => {
    await clearClientRuntimeCaches();
    window.location.reload();
  };

  render() {
    if (this.state.error) {
      return (
        <div className="min-h-screen bg-panel-bg text-panel-text grid place-items-center p-6">
          <div className="max-w-xl w-full rounded-2xl border border-panel-danger/40 bg-panel-card p-5 space-y-3">
            <h1 className="font-semibold text-panel-danger">
              Sayfa yüklenemedi
            </h1>
            <p className="text-sm text-panel-muted break-words">
              Beklenmeyen bir arayüz hatası oluştu. Sayfayı yenileyin; sorun
              sürerse aşağıdaki teknik detayı paylaşın.
            </p>
            <pre className="text-xs whitespace-pre-wrap break-all rounded-lg bg-panel-bg border border-panel-border p-3 text-panel-warning">
              {this.state.error.message || String(this.state.error)}
            </pre>
            <button
              onClick={() => void this.recover()}
              className="min-h-11 px-4 rounded-lg bg-panel-accent text-panel-on-accent text-sm"
            >
              Önbelleği Temizle ve Yeniden Aç
            </button>
          </div>
        </div>
      );
    }
    return this.props.children;
  }
}

function PrivateRoute({ children }: { children: React.ReactNode }) {
  const { token } = useAuth();
  const current =
    window.location.pathname + window.location.search + window.location.hash;
  return token ? (
    <Layout>{children}</Layout>
  ) : (
    <Navigate to={`/login?next=${encodeURIComponent(current)}`} replace />
  );
}

function safeLoginNext(): string {
  const value = new URLSearchParams(window.location.search).get("next") || "";
  if (!value.startsWith("/") || value.startsWith("//")) return "/";
  try {
    const target = new URL(value, window.location.origin);
    return target.origin === window.location.origin
      ? target.pathname + target.search + target.hash
      : "/";
  } catch {
    return "/";
  }
}

const HOST_STATUS_MISS_KEY = "sp_host_status_miss";

/** Kök yolda: bu host bir durum sayfasına mı ait? Değilse normal login akışı. */
function HostStatusGate() {
  const [state, setState] = useState<"checking" | "status" | "panel">(() =>
    sessionStorage.getItem(HOST_STATUS_MISS_KEY) === window.location.host
      ? "panel"
      : "checking",
  );
  useEffect(() => {
    if (state !== "checking") return;
    let alive = true;
    api
      .get(
        `/observability/public/status/resolve?host=${encodeURIComponent(window.location.host)}`,
      )
      .then(() => alive && setState("status"))
      .catch(() => {
        sessionStorage.setItem(HOST_STATUS_MISS_KEY, window.location.host);
        if (alive) setState("panel");
      });
    return () => {
      alive = false;
    };
  }, [state]);
  if (state === "checking") return null;
  return state === "status" ? (
    <PublicStatus byHost />
  ) : (
    <Navigate to="/login" replace />
  );
}

// Kapasite gerektiren rotalar. authReady false iken bunlardan hiçbiri
// değerlendirilemez: capabilities dizisi henüz boş olduğu için "erişim yok"
// ile "henüz öğrenilmedi" ayırt edilemez. Liste App.tsx dışına da taşınabilir
// ama guard ile aynı dosyada kalması ikisinin birbirinden kopmasını önlüyor.
const CAPABILITY_GATED_PATHS = new Set([
  "/users",
  "/storage",
  "/cloudflare",
  "/roles",
  "/dns",
  "/security-center",
  "/security/waf",
  "/waf",
  "/platform",
  "/platform/mail",
  "/platform/android",
  "/android-devices",
  "/app-catalog",
  "/public-api",
]);

function RouteLoading() {
  return (
    <div className="min-h-[60vh] grid place-items-center text-panel-muted text-sm">
      Yükleniyor…
    </div>
  );
}

function AppRoutes() {
  const { token, hasCapability, isPlatformAdmin, authReady } = useAuth();
  const { pathname } = useLocation();
  if (pathname.startsWith("/status/"))
    return <PublicStatus slug={pathname.slice("/status/".length)} />;
  // uptime.servernox.com alt alan adları ve müşterinin kendi alan adı: kök
  // yolda oturum yoksa sayfa HOST'tan çözülür. Panelin kendi host'unda bu
  // istek 404 döner, sonuç oturum boyunca hatırlanır ve normal akış (login)
  // devam eder — yani ekstra istek yalnızca oturumsuz İLK kök ziyarette olur.
  if (!token && pathname === "/") return <HostStatusGate />;
  if (pathname === "/login")
    return token ? <Navigate to={safeLoginNext()} replace /> : <Login />;
  // /auth/context henüz cevap vermedi (sayfa yeni açıldı, yenilendi, ya da
  // /users gibi bir sayfaya doğrudan bağlantıyla gelindi). O ana kadar
  // capabilities [] görünür ve bu, aşağıdaki kontrollerin gerçek bir
  // erişim reddiyle ayırt edilemeyen bir "henüz bilinmiyor" durumuyla
  // karıştırılıp kullanıcıyı sessizce ana sayfaya atmasına yol açıyordu.
  if (token && !authReady && CAPABILITY_GATED_PATHS.has(pathname))
    return (
      <PrivateRoute>
        <RouteLoading />
      </PrivateRoute>
    );
  if (token && pathname === "/users" && !hasCapability("member.read"))
    return <Navigate to="/" replace />;
  if (token && pathname === "/roles" && !hasCapability("role.read"))
    return <Navigate to="/" replace />;
  if (token && pathname === "/waf")
    return <Navigate to="/security/waf" replace />;
  if (token && pathname === "/dns" && !hasCapability("dns.zone.read"))
    return <Navigate to="/" replace />;
  if (token && pathname === "/storage" && !hasCapability("storage.object.read"))
    return <Navigate to="/" replace />;
  if (
    token &&
    pathname === "/cloudflare" &&
    !hasCapability("dns.connection.read") &&
    !hasCapability("storage.connection.read")
  )
    return <Navigate to="/" replace />;
  if (
    token &&
    pathname === "/security-center" &&
    !hasCapability("server.admin") &&
    !hasCapability("firewall.read") &&
    !hasCapability("apparmor.read") &&
    !hasCapability("sudoers.read") &&
    !hasCapability("os.users.read") &&
    !hasCapability("ssh_keys.read")
  )
    return <Navigate to="/" replace />;
  if (
    token &&
    pathname === "/security/waf" &&
    !hasCapability("security.waf.view")
  )
    return <Navigate to="/" replace />;
  if (token && pathname === "/app-catalog" && !hasCapability("docker.operate"))
    return <Navigate to="/" replace />;
  if (token && pathname === "/platform" && !isPlatformAdmin)
    return <Navigate to="/" replace />;
  if (token && pathname === "/platform/mail" && !isPlatformAdmin)
    return <Navigate to="/" replace />;
  if (token && pathname === "/platform/android" && !isPlatformAdmin && !hasCapability("android_devices.read"))
    return <Navigate to="/" replace />;
  if (token && pathname === "/android-devices" && !hasCapability("android_devices.read"))
    return <Navigate to="/" replace />;
  if (token && pathname === "/public-api" && !hasCapability("ai.api_keys.manage"))
    return <Navigate to="/" replace />;
  const pages: Record<string, React.ReactNode> = {
    "/": <Dashboard />,
    "/dashboard": <Dashboard />,
    "/cleaner": <Dashboard />,
    "/nginx": <Nginx />,
    "/docker": <Docker />,
    "/app-catalog": <AppCatalog />,
    "/supervisor": <Supervisor />,
    "/mariadb": <MariaDB />,
    "/postgresql": <PostgreSQL />,
    "/caddy": <Caddy />,
    "/dns": <Dns />,
    "/logs": <Logs />,
    "/settings": <Settings />,
    "/redis": <Redis />,
    "/php": <PHP />,
    "/backup": <Backup />,
    "/wireguard": <WireGuard />,
    "/systemd": <Systemd />,
    "/websites": <Websites />,
    "/migrations": <Migrations />,
    "/ssl": <SSL />,
    "/network": <Network />,
    "/pm2": <PM2Manager />,
    "/helpers": <Helpers />,
    "/cron": <CronJobs />,
    "/notifications": <NotificationHistory />,
    "/notifications/history": <NotificationHistory />,
    "/notifications/settings": <Notifications />,
    "/users": <Users />,
    "/roles": <Roles />,
    "/files": <Files />,
    "/storage": <Storage />,
    "/cloudflare": <CloudflareCenter />,
    "/git": <Git />,
    "/git/oauth/callback": <Git />,
    "/scripts": <Scripts />,
    "/api-test": <ApiTest />,
    "/public-api": <PublicAPI />,
    "/servers": <Servers />,
    "/packages": <Packages />,
    "/component-center": <ComponentCenter />,
    "/timers": <Timers />,
    "/plugins": <Plugins />,
    "/deployments": <Deployments />,
    "/security-center": <SecurityCenter />,
    "/security/waf": <Waf />,
    "/observability": <Observability />,
    "/platform": <Platform />,
    "/platform/mail": <MailControlCenter />,
    "/platform/android": <AndroidManagement />,
    "/android-devices": <Navigate to="/platform/android#remote" replace />,
    "/load-balancers": <LoadBalancers />,
    "/vscode": <VsCode />,
    "/ai-coding": <AiCoding />,
    "/terminal": <TerminalPage />,
  };
  const page = pages[pathname];
  return page ? (
    <PrivateRoute>{page}</PrivateRoute>
  ) : (
    <Navigate to="/" replace />
  );
}

export default function App() {
  return (
    <AppErrorBoundary>
      <ThemeProvider>
        <LocaleProvider>
          <AuthProvider>
            <ServerProvider>
              <ConfirmProvider>
                <JobsProvider>
                  <RouterProvider>
                    <Suspense fallback={<RouteLoading />}>
                      <AppRoutes />
                    </Suspense>
                    <Suspense fallback={null}>
                      <JobsWidget />
                      <DownloadTransferTray />
                      <GlobalMediaPlayerHost />
                    </Suspense>
                  </RouterProvider>
                </JobsProvider>
              </ConfirmProvider>
            </ServerProvider>
          </AuthProvider>
        </LocaleProvider>
      </ThemeProvider>
    </AppErrorBoundary>
  );
}
